<template>
  <div class="w-full" :key="reRenderKey">
    <div v-if="shownAlert" class="mb-12">
      <zt-alert
        :color="statusAlert"
        close-icon
        filled
        @afterClosed="shownAlert = false"
        class="message px-3 py-3"
      >
        <div slot="custom-content">
          <div class="flex justify-start items-center">
            <zt-icon
              v-if="iconName"
              :name="iconName"
              class="icon mr-3"
            ></zt-icon>
            <p>{{ messageAlert }}</p>
          </div>
        </div>
      </zt-alert>
    </div>
    <div class="w-full custom-input mb-12">
      <validation-provider
        ref="fieldCountry"
        :rules="{ required: true }"
        v-slot="{ errors, failedRules, invalid }"
      >
        <zt-select
          class="w-full"
          :label="$t('message.selfService.fields.country')"
          :outlined="true"
          :return-object="true"
          :color="invalid ? 'danger' : 'primary'"
          :items="countryList"
          :disabled="countryDisabled"
          item-text="description"
          item-value="id"
          v-model="params.idCountry"
          data-vs-select="country"
          @change="setStateList"
        />
        <validation-message
          :rules="{ errors, failedRules, invalid, fieldName: 'country' }"
        />
      </validation-provider>
    </div>
    <div class="w-full custom-input mb-12">
      <validation-provider
        ref="fieldState"
        :rules="{ required: true }"
        v-slot="{ errors, failedRules, invalid }"
      >
        <zt-select
          class="w-full"
          :label="$t('message.selfService.fields.state')"
          :outlined="true"
          :return-object="true"
          :color="invalid ? 'danger' : 'primary'"
          :items="stateList"
          :disabled="stateDisabled"
          :key="stateKey"
          item-text="description"
          item-value="id"
          v-model="params.idState"
          data-vs-select="state"
          @change="setCityList"
        />
        <validation-message
          :rules="{ errors, failedRules, invalid, fieldName: 'state' }"
        />
      </validation-provider>
    </div>
    <div class="w-full custom-input mb-12">
      <validation-provider
        ref="fieldCity"
        :rules="{ required: true }"
        v-slot="{ errors, failedRules, invalid }"
      >
        <zt-select
          class="w-full"
          :label="$t('message.selfService.fields.city')"
          :outlined="true"
          :return-object="true"
          :color="invalid ? 'danger' : 'primary'"
          :items="cityList"
          :disabled="cityDisabled"
          :key="cityKey"
          item-text="description"
          item-value="id"
          v-model="params.idCity"
          data-vs-select="city"
        />
        <validation-message
          :rules="{ errors, failedRules, invalid, fieldName: 'city' }"
        />
      </validation-provider>
    </div>
    <div class="w-full custom-input mb-12">
      <validation-provider
        ref="fieldStreet"
        :rules="{
          required: true,
          min: 3,
          max: 250,
          regex: /^[0-9a-zA-Z-À-ú-\s]+$/
        }"
        v-slot="{ errors, failedRules, invalid }"
      >
        <zt-input
          class="w-full"
          :color="invalid ? 'danger' : 'primary'"
          outlined
          :label="$t('message.selfService.fields.street')"
          v-model="params.street"
          data-vs-input="street"
        />
        <validation-message
          :rules="{ errors, failedRules, invalid, fieldName: 'streetVp' }"
        />
      </validation-provider>
    </div>
    <div class="w-full flex">
      <div class="w-full custom-input mb-12 mr-4 w-1/3">
        <ValidationProvider
          ref="fieldNumber"
          :rules="{ required: true, min: 1, max: 8, regex: /^\d+$/ }"
          v-slot="{ errors, failedRules, invalid }"
        >
          <zt-input
            class="w-full"
            :color="invalid ? 'danger' : 'primary'"
            outlined
            :label="$t('message.selfService.fields.number')"
            v-model="params.number"
            data-vs-input="number"
          />
          <validation-message
            :rules="{ errors, failedRules, invalid, fieldName: 'numberVp' }"
          />
        </ValidationProvider>
      </div>
      <div class="w-full custom-input mb-12 w-1/3">
        <ValidationProvider
          ref="fieldFlat"
          :rules="{ min: 1, max: 8, regex: /^[0-9a-zA-Z-À-ú-\s]+$/ }"
          v-slot="{ errors, failedRules, invalid }"
        >
          <zt-input
            class="w-full"
            :color="invalid ? 'danger' : 'primary'"
            outlined
            :label="$t('message.selfService.fields.flat')"
            v-model="params.flat"
            data-vs-input="flat"
          />
          <validation-message
            :rules="{ errors, failedRules, invalid, fieldName: 'flat' }"
          />
        </ValidationProvider>
      </div>
      <div class="w-full custom-input mb-12 ml-4 w-1/3">
        <ValidationProvider
          ref="fieldDepartment"
          :rules="{ min: 1, max: 8, regex: /^[0-9a-zA-Z-À-ú-\s]+$/ }"
          v-slot="{ errors, failedRules, invalid }"
        >
          <zt-input
            class="w-full"
            :color="invalid ? 'danger' : 'primary'"
            outlined
            :label="$t('message.selfService.fields.department')"
            v-model="params.department"
            data-vs-input="department"
          />
          <validation-message
            :rules="{ errors, failedRules, invalid, fieldName: 'department' }"
          />
        </ValidationProvider>
      </div>
    </div>
    <div class="w-full custom-input mb-12">
      <ValidationProvider
        ref="fieldNeighborhood"
        :rules="{ min: 1, max: 30, regex: /^[0-9a-zA-Z-À-ú-\s]+$/ }"
        v-slot="{ errors, failedRules, invalid }"
      >
        <zt-input
          class="w-full"
          :color="invalid ? 'danger' : 'primary'"
          outlined
          :label="$t('message.selfService.fields.neighborhood')"
          v-model="params.neighborhood"
          data-vs-input="neighborhood"
        />
        <validation-message
          :rules="{ errors, failedRules, invalid, fieldName: 'neighborhood' }"
        />
      </ValidationProvider>
    </div>
    <div class="w-full custom-input mb-12">
      <ValidationProvider
        ref="fieldBlock"
        :rules="{ min: 1, max: 50, regex: /^[0-9a-zA-Z-À-ú-\s]+$/ }"
        v-slot="{ errors, failedRules, invalid }"
      >
        <zt-input
          class="w-full"
          :color="invalid ? 'danger' : 'primary'"
          outlined
          :label="$t('message.selfService.fields.block')"
          v-model="params.block"
          data-vs-input="block"
        />
        <validation-message
          :rules="{ errors, failedRules, invalid, fieldName: 'block' }"
        />
      </ValidationProvider>
    </div>
    <div class="w-full custom-input mb-12">
      <ValidationProvider
        ref="fieldAddressType"
        :rules="{ required: true }"
        v-slot="{ errors, failedRules, invalid }"
      >
        <zt-select
          class="w-full"
          :label="$t('message.selfService.fields.addressType')"
          :outlined="true"
          :return-object="true"
          :color="invalid ? 'danger' : 'primary'"
          :items="addressTypeList"
          :disabled="true"
          item-text="description"
          item-value="id"
          v-model="params.addressType"
          data-vs-select="addressType"
        />

        <validation-message
          :rules="{ errors, failedRules, invalid, fieldName: 'type_domicile' }"
        />
      </ValidationProvider>
      <p class="text-zt-danger mt-2" v-if="!isNotAvailable && (isArg || isLatam)">{{ $t('message.selfService.changeAdressWarning') }}</p>
    </div>
    <div class="flex justify-end w-full">
      <zt-button
        v-if="pendingApprove"
        @click="cancelDialog = true"
        data-vs-button="cancel-request-approve"
      >
        Cancelar solicitud
      </zt-button>
      <zt-button
        v-else
        :disabled="isNotAvailable"
        :loader="isLoading"
        @click="signOrSave"
        data-vs-button="save-address"
      >
        {{ $t('message.selfService.actions.save') }}
      </zt-button>
    </div>

    <self-service-cancel-dialog
      v-if="pendingApprove"
      :active="cancelDialog"
      @close-modal="closeModal"
      @confirm="cancelRequest"
    />
    <sign-modal :activeSign="activeSign" :params="params" :hasVP="true"
    @close-sign-modal="activeSign = false"  />

  </div>
</template>

<script>
import { isEmpty, isEqual, find, isObject } from 'lodash'
import Users from '@/apiClient/Users'
import SichernIntegration from '@/apiClient/SichernIntegration'
import ValidationMessage from '../../generic/ValidationMessage'
import SelfServiceCancelDialog from '@/modules/SelfService/components/SelfServiceCancelDialog'
import parseNull from '@/utils/parseNull'
import SignModal from '@/modules/SelfService/components/sichernpeople/SignModal'
const icons = {
  danger: 'exclamation-circle-solid',
  info: 'info-circle-solid'
}
export default {
  name: 'SelfServiceVpAddressChange',
  components: { ValidationMessage, SelfServiceCancelDialog, SignModal },
  props: {
    info: {
      type: Object,
      default: () => ({})
    },
    updateFn: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      isLoading: false,
      isNotAvailable: true,
      countryList: [],
      stateList: [],
      cityList: [],
      addressTypeList: [],
      params: {
        idCountry: '',
        idState: '',
        idCity: '',
        street: '',
        number: '',
        flat: '',
        department: '',
        neighborhood: '',
        block: '',
        addressType: {
          id: 2,
          description: 'Particular'
        }
      },
      pendingApprove: false,
      shownAlert: false,
      statusAlert: 'info',
      messageAlert: '',
      cache: {},
      cancelDialog: false,
      cancelRequesting: false,
      activeSign:false,
      reRenderKey: 0,
      cityKey: 0,
      stateKey: 0,
    }
  },
  watch: {
    params: {
      async handler(newParams) {
        if (this.info.pending && !isEqual(this.cache, this.params)) {
          this.pendingApprove = false
        } else {
          this.pendingApprove = this.info.pending
        }
        for (const [key] of Object.entries(newParams)) {
          if (key) {
            const isError = await this.checkFieldError()
            this.isNotAvailable = isError || isEqual(this.cache, this.params)
            if (isError) break
          }
        }
      },
      deep: true
    },
    info() {
      this.infoUpdate()
    }
  },
  computed: {
    iconName() {
      return icons[this.statusAlert]
    },
    addressTypeDisabled() {
      return isEmpty(this.addressTypeList)
    },
    countryDisabled() {
      return isEmpty(this.countryList)
    },
    stateDisabled() {
      return isEmpty(this.params.idCountry) && isEmpty(this.stateList)
    },
    cityDisabled() {
      return (
        isEmpty(this.params.idCountry) &&
        isEmpty(this.params.idState) &&
        isEmpty(this.cityList)
      )

    },
    isArg() {
      return this.$store.state.user.pais === 'AR'
    },
    isLatam() {
      return this.$store.state.user.pais === 'LA'
    }
  },
  async created() {
    
    this.userApi = new Users()
    this.vp = new SichernIntegration()
    await this.getAddressTypes()
    await this.getCountries()
    if (!isEmpty(this.info.idState)) {
      await this.setStateList(this.info.idCountry)
    }
    if (!isEmpty(this.info.idCity)) {
      await this.setCityList(this.info.idCountry, this.info.idState)
    }
    this.infoUpdate()
  },
  methods: {
    signOrSave(){
    this.isArg || this.isLatam ? this.activeSign = true : this.save()
    },
    async save() {
      if (this.isNotAvailable) return
      this.isLoading = true
      this.shownAlert = false
      const body = {
        idCountry: this.params.idCountry.id,
        idState: this.params.idState.id,
        idCity: this.params.idCity.id,
        street: this.params.street,
        number: this.params.number,
        flat: this.params.flat,
        department: this.params.department,
        neighborhood: this.params.neighborhood,
        block: this.params.block
      }
      if (body.id) delete body.id
      if ('sichernpeople' in body) delete body.sichernpeople
      try {
        await this.userApi.saveAddressInfo(body)
        await this.updateFn()
        this.isLoading = false
        this.isNotAvailable = true
        this.shownAlert = true
        this.statusAlert = 'info'
        this.messageAlert = this.$t('message.selfService.response.success')
      } catch (e) {
        const code = e.response.data.codes[0].code
        this.messageAlert = this.$t(
          `message.selfService.response.errors.${code}`
        )
        this.isLoading = false
        this.isNotAvailable = false
        this.shownAlert = true
        this.statusAlert = 'danger'
      }
    },
    async checkFieldError() {
      for (const [, provider] of Object.entries(this.$refs)) {
        if (provider) {
          const { valid } = await provider.validate()
          if (!valid) return true
        }
      }
      return false
    },
    infoUpdate() {
      const { pending, ...rest } = this.info
      this.params = parseNull({ ...rest })
      if (!isEmpty(this.info.idCountry) && this.countryList.length) {
        this.params.idCountry = find(this.countryList, {
          id: parseInt(this.info.idCountry)
        })
      }
      if (!isEmpty(this.info.idState) && this.stateList.length) {
        this.params.idState = find(this.stateList, {
          id: parseInt(this.info.idState)
        })
      }
      if (!isEmpty(this.info.idCity) && this.cityList.length) {
        this.params.idCity = find(this.cityList, {
          id: parseInt(this.info.idCity)
        })
      }
      if (this.addressTypeList.length) {
        this.params.addressType = find(this.addressTypeList, {
          id: 2
        })
      }
      this.cache = { ...this.params }
      this.pendingApprove = pending
      this.messageAlert = this.$t('message.selfService.response.success')
      this.statusAlert = 'info'
      this.shownAlert = pending
      this.reRenderKey += 1
    },
    async cancelRequest() {
      this.cancelRequesting = true
      try {
        await this.userApi.cancelSelfServiceRequest(this.info.id)
        await this.updateFn()
        this.cancelRequesting = false
        this.closeModal()
        this.messageAlert = this.$t(
          'message.selfService.response.successCancel'
        )
        this.statusAlert = 'info'
        this.shownAlert = true
      } catch (e) {
        if (e.response.status === 404) {
          this.messageAlert = this.$t(
            `message.selfService.response.errors.notFound`
          )
        } else {
          const code = e.response.data.codes[0].code
          this.messageAlert = this.$t(
            `message.selfService.response.errors.${code}`
          )
        }
        this.isLoading = false
        this.isNotAvailable = false
        this.shownAlert = true
        this.statusAlert = 'danger'
        this.cancelRequesting = false
        this.closeModal()
      }
    },
    closeModal() {
      if (this.cancelRequesting) return
      this.cancelRequesting = false
      this.cancelDialog = false
    },
    async getCountries() {
      this.countryList = []
      try {
        const {
          data: { data }
        } = await this.vp.getCountries()
        this.countryList = data
      } catch (e) {}
    },
    async getAddressTypes() {
      this.addressTypeList = []
      try {
        const {
          data: { data }
        } = await this.vp.getAddressTypes()
        this.addressTypeList = data.filter(type => type.id === 2)
      } catch (e) {}
    },
    async setStateList(id) {
      this.stateList = []
      this.cityList = []
      this.params.idState = ''
      this.params.idCity = ''
      this.stateKey += 1
      this.cityKey += 1
      try {
        const {
          data: { data }
        } = await this.vp.getStates({
          idCountry: !isObject(id) ? id : this.params.idCountry.id
        })
        this.stateList = data
      } catch (e) {}
    },
    async setCityList(countryId, stateId) {
      this.cityList = []
      this.params.idCity = ''
      this.cityKey += 1
      try {
        const {
          data: { data }
        } = await this.vp.getCities({
          idCountry: !isObject(countryId) ? countryId : this.params.idCountry.id,
          idState: stateId && !isObject(stateId) ? stateId : this.params.idState.id
        })
        this.cityList = data
      } catch (e) {}
    }
  }
}
</script>

<style scoped lang="scss">
.custom-input {
  ::v-deep {
    .zt-label.zt-label-active {
      background: #ffff !important;
      padding: 2px !important;
    }
    .zt-input {
      font-size: 1.125rem;
    }
  }
}
</style>
